import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "dsf_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, quantity = 1, options = {}) => {
    setItems((prev) => {
      const key = product.id + JSON.stringify(options);
      const idx = prev.findIndex((it) => it.product_id + JSON.stringify(it.options) === key);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity,
          options,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((product_id, options = {}) => {
    setItems((prev) =>
      prev.filter(
        (it) => !(it.product_id === product_id && JSON.stringify(it.options) === JSON.stringify(options))
      )
    );
  }, []);

  const updateQty = useCallback((product_id, options, quantity) => {
    setItems((prev) =>
      prev.map((it) =>
        it.product_id === product_id && JSON.stringify(it.options) === JSON.stringify(options)
          ? { ...it, quantity: Math.max(1, quantity) }
          : it
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);
  const count = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
